import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import { getFiyatlandirmaPage } from '../services/api';
import DEFAULT_DATA from '../default_datas/pages/defaultFiyatlandirma';

const Fiyatlandirma = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yillik, setYillik] = useState(false);
  const [acikSSS, setAcikSSS] = useState(null);

  const tx = (tr, en) => lang === 'en' ? (en || tr) : tr;

  useEffect(() => {
    getFiyatlandirmaPage()
      .then(result => {
        if (
          result &&
          Array.isArray(result.pageContent) &&
          result.pageContent.length > 0
        ) {
          setData(result);
        } else {
          setData(DEFAULT_DATA);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Veri yükleme hatası:', err);
        setData(DEFAULT_DATA);
        setLoading(false);
      });
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = Array.isArray(data?.pageContent) ? data.pageContent : (Array.isArray(DEFAULT_DATA?.pageContent) ? DEFAULT_DATA.pageContent : []);

  const hero = sections.find(s => s.__component === 'sections.hero');
  const pricingSection = sections.find(s => s.__component === 'sections.pricing-section');
  const comparisonSection = sections.find(s => s.__component === 'sections.comparison-section');
  const faqSection = sections.find(s => s.__component === 'sections.faq-section');
  const cta = sections.find(s => s.__component === 'sections.cta');

  const planStiller = [
    { border: 'border-[#e0eeeb]', buton: 'bg-white text-[#2a9d8f] border-2 border-[#2a9d8f] hover:bg-[#2a9d8f] hover:text-white' },
    { border: 'border-[#2a9d8f]', buton: 'bg-[#2a9d8f] text-white hover:bg-[#238a7e]' },
    { border: 'border-[#0D3A2F]', buton: 'bg-[#0D3A2F] text-white hover:bg-[#15614F]' },
  ];

  const renderDeger = (deger, deger_en) => {
    const gosterilecek = lang === 'en' ? (deger_en || deger) : deger;
    if (gosterilecek === 'true' || gosterilecek === true) return <span className="material-symbols-outlined text-[#2a9d8f] text-xl">check_circle</span>;
    if (gosterilecek === 'false' || gosterilecek === false) return <span className="material-symbols-outlined text-gray-300 text-xl">cancel</span>;
    return <span className="text-sm font-semibold text-[#0D3A2F]">{gosterilecek}</span>;
  };

  const colHeaders = lang === 'en' ? ['Starter', 'Professional', 'Enterprise'] : ['Başlangıç', 'Profesyonel', 'Kurumsal'];

  return (
    <div className="min-h-screen bg-[#f5faf9] font-poppins text-[#0D3A2F]">

      {/* ── 1. HERO ── */}
      {hero && (
        <ScrollReveal>
          <section className="bg-white border-b border-[#e0eeeb] py-10 sm:py-16 md:py-20 px-4 sm:px-6 md:px-28 text-center">
            {hero.badge && (
              <span className="inline-block bg-[#e0f2ef] text-[#2a9d8f] text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1.5 rounded-full mb-4 sm:mb-5 tracking-widest uppercase">
                {tx(hero.badge, hero.badge_en)}
              </span>
            )}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#0D3A2F] mb-3 sm:mb-4 leading-tight">
              {tx(hero.title, hero.title_en)}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-[#4a6b63] max-w-xl mx-auto mb-6 sm:mb-10 leading-relaxed px-2">
              {tx(hero.description, hero.description_en)}
            </p>
            <div className="inline-flex items-center gap-2 sm:gap-4 bg-[#f5faf9] border border-[#e0eeeb] rounded-xl p-1 sm:p-1.5">
              <button
                onClick={() => setYillik(false)}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all border-none cursor-pointer ${!yillik ? 'bg-white shadow text-[#0D3A2F]' : 'text-[#4a6b63] bg-transparent'}`}
              >
                {lang === 'en' ? 'Monthly' : 'Aylık'}
              </button>
              <button
                onClick={() => setYillik(true)}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-1 sm:gap-2 border-none cursor-pointer ${yillik ? 'bg-white shadow text-[#0D3A2F]' : 'text-[#4a6b63] bg-transparent'}`}
              >
                {lang === 'en' ? 'Yearly' : 'Yıllık'}
                <span className="bg-[#2a9d8f] text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                  {lang === 'en' ? '17%' : '%17'}
                </span>
              </button>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* ── 2. PLAN KARTLARI ── */}
      {pricingSection?.plans?.length > 0 && (
        <ScrollReveal>
          <section className="py-10 sm:py-16 px-4 sm:px-6 md:px-28">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {pricingSection.plans.map((plan, i) => {
                const stil = planStiller[i] || planStiller[0];
                const isOzel = plan.priceMonthly === 'Özel Fiyat' || plan.priceMonthly === 'Custom Price';

                return (
                  <div key={plan.id} className={`bg-white rounded-xl sm:rounded-2xl border-2 ${stil.border} p-5 sm:p-8 flex flex-col relative ${plan.isPopular ? 'shadow-2xl md:scale-105 order-first md:order-none' : 'shadow-sm'}`}>
                    {plan.isPopular && (
                      <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
                        <span className="bg-[#2a9d8f] text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full whitespace-nowrap">
                          {lang === 'en' ? 'Most Popular' : 'En Popüler'}
                        </span>
                      </div>
                    )}
                    <div className="mb-4 sm:mb-6">
                      <h2 className="text-xl sm:text-2xl font-black text-[#0D3A2F] mb-2">{tx(plan.name, plan.name_en)}</h2>
                      <p className="text-xs sm:text-sm text-[#4a6b63] leading-relaxed">{tx(plan.description, plan.description_en)}</p>
                    </div>
                    <div className="mb-6 sm:mb-8">
                      <span className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0D3A2F]">
                        {isOzel ? (lang === 'en' ? 'Custom' : 'Özel Fiyat') : (yillik ? plan.priceYearly : plan.priceMonthly)}
                      </span>
                    </div>
                    <button onClick={() => navigate('/iletisim')} className={`w-full py-3 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base mb-6 sm:mb-8 transition-all border-none cursor-pointer ${stil.buton}`}>
                      {tx(plan.buttonLabel, plan.buttonLabel_en)}
                    </button>
                    <div className="flex flex-col gap-2 sm:gap-3 flex-1">
                      {(plan.features || []).map((f, j) => (
                        <div key={j} className={`flex items-start gap-2 sm:gap-2.5 ${!f.available ? 'opacity-40' : ''}`}>
                          <span className={`material-symbols-outlined text-base sm:text-lg flex-shrink-0 mt-0.5 ${f.available ? 'text-[#2a9d8f]' : 'text-gray-400'}`}>
                            {f.available ? 'check_circle' : 'cancel'}
                          </span>
                          <span className={`text-xs sm:text-sm ${f.available ? 'text-[#0D3A2F]' : 'text-[#4a6b63] line-through'}`}>
                            {tx(f.text, f.text_en)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* ── 3. KARŞILAŞTIRMA TABLOSU ── */}
      {comparisonSection?.rows?.length > 0 && (
        <ScrollReveal>
          <section className="py-10 sm:py-16 px-4 sm:px-6 md:px-28 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-[#0D3A2F] mb-2 text-center">{tx(comparisonSection.title, comparisonSection.title_en)}</h2>
              <p className="text-xs sm:text-sm md:text-base text-[#4a6b63] text-center mb-8 sm:mb-12 px-2">{tx(comparisonSection.subtitle, comparisonSection.subtitle_en)}</p>
              <div className="overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
                <div className="rounded-xl sm:rounded-2xl border border-[#e0eeeb] overflow-hidden min-w-[600px] sm:min-w-0">
                  <div className="grid grid-cols-4 bg-[#f5faf9] px-3 sm:px-6 py-3 sm:py-4 border-b border-[#e0eeeb]">
                    <div className="text-[10px] sm:text-xs md:text-sm font-bold text-[#4a6b63] uppercase tracking-wide">{lang === 'en' ? 'Feature' : 'Özellik'}</div>
                    {colHeaders.map(p => <div key={p} className="text-[10px] sm:text-xs md:text-sm font-bold text-[#0D3A2F] text-center">{p}</div>)}
                  </div>
                  {comparisonSection.rows.map((satir, i) => (
                    <div key={satir.id} className={`grid grid-cols-4 px-3 sm:px-6 py-3 sm:py-4 border-b border-[#f0f0f0] items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#f5faf9]'}`}>
                      <div className="text-[10px] sm:text-xs md:text-sm font-semibold text-[#0D3A2F] pr-2">{tx(satir.feature, satir.feature_en)}</div>
                      <div className="flex justify-center">{renderDeger(satir.starter, satir.starter_en)}</div>
                      <div className="flex justify-center">{renderDeger(satir.pro, satir.pro_en)}</div>
                      <div className="flex justify-center">{renderDeger(satir.enterprise, satir.enterprise_en)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* ── 4. SSS ── */}
      {faqSection?.items?.length > 0 && (
        <ScrollReveal>
          <section className="py-10 sm:py-16 px-4 sm:px-6 md:px-28">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-[#0D3A2F] mb-2 text-center">{tx(faqSection.title, faqSection.title_en)}</h2>
              <p className="text-xs sm:text-sm md:text-base text-[#4a6b63] text-center mb-6 sm:mb-10">{tx(faqSection.subtitle, faqSection.subtitle_en)}</p>
              <div className="flex flex-col gap-2 sm:gap-3">
                {faqSection.items.map((item, i) => (
                  <div key={item.id} className="bg-white border border-[#e0eeeb] rounded-lg sm:rounded-xl overflow-hidden">
                    <button onClick={() => setAcikSSS(acikSSS === i ? null : i)} className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 text-left font-semibold text-[#0D3A2F] bg-transparent border-none cursor-pointer hover:bg-[#f5faf9] transition-colors text-sm sm:text-base">
                      <span className="pr-2">{tx(item.question, item.question_en)}</span>
                      <span className="material-symbols-outlined text-[#2a9d8f] flex-shrink-0">{acikSSS === i ? 'expand_less' : 'expand_more'}</span>
                    </button>
                    {acikSSS === i && <div className="px-4 sm:px-6 pb-4 sm:pb-5 text-xs sm:text-sm text-[#4a6b63] border-t border-[#f0f0f0] pt-3 sm:pt-4">{tx(item.answer, item.answer_en)}</div>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* ── 5. CTA ── */}
      {cta && (
        <section className="bg-[#0D3A2F] py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-28 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-white mb-3 sm:mb-4">{tx(cta.Title, cta.Title_en)}</h2>
          <p className="text-white/70 text-xs sm:text-sm md:text-base mb-6 sm:mb-8 max-w-lg mx-auto px-2">{tx(cta.Description, cta.Description_en)}</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button onClick={() => navigate('/iletisim')} className="bg-[#2a9d8f] text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-xl border-none cursor-pointer hover:bg-[#238a7e] transition-colors text-sm sm:text-base">
              {tx(cta.primaryButtonText, cta.primaryButtonText_en)}
            </button>
            <button onClick={() => navigate('/iletisim')} className="bg-transparent text-white border-2 border-white/30 font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-xl cursor-pointer hover:bg-white/10 transition-colors text-sm sm:text-base">
              {tx(cta.secondaryButtonText, cta.secondaryButtonText_en)}
            </button>
          </div>
        </section>
      )}

    </div>
  );
};

export default Fiyatlandirma;